// Wizard.jsx

import React from 'react';

// CSS
import './Wizard.css';

// Components
import { AnimatePresence } from 'framer-motion';

/**
 * Wizard
 *
 * Animated multi-step wizard container that passes step state to child steps.
 *
 * @param {ReactNode} children - WizardStep children to render.
 * @param {number} direction - Navigation direction used for step transitions.
 * @param {number|string} height - Container height strategy.
 * @param {number} step - Active step index.
 *
 * @returns {ReactNode} The rendered wizard container.
 */
const Wizard = ({ height, step, direction, children }) => {

  // Interpret height prop:
  // - number => px height
  // - 'auto' => CSS auto
  // - undefined/null => fill available parent space (flex: 1)
  // - string (e.g. '100%') => use directly
  const style = {};
  if (typeof height === 'number') {
    style.height = `${height}px`;
  } else if (height === 'auto') {
    style.height = 'auto';
    style.overflow = 'visible';
  } else if (height === undefined || height === null) {
    // default to filling available space so children can use flex-based sizing
    style.height = '100%';
    style.flex = 1;
    style.minHeight = 0;
  } else {
    style.height = height;
  }

  const isAutoHeight = height === 'auto';

  // ----- RENDER ----- //

  return (
    <div className="wizard-container" style={style}>
      <AnimatePresence initial={false} custom={direction}>
        {React.Children.map(children, (child) =>
          React.cloneElement(child, { currentStep: step, direction, isAutoHeight })
        )}
      </AnimatePresence>
    </div>
  );
};

// Dev Tools
Wizard.displayName = 'Wizard';

export default Wizard;
